import { createContext, useContext, useState, type ReactNode } from "react"
import { NOTIFICATION_CENTER_OPEN_STORAGE_KEY } from "@/v2-backoffice-client/notification/notification.constants"

type NotificationCenterContextValue = {
  isOpen: boolean
  open(): void
  close(): void
  toggle(): void
}

const NotificationCenterContext = createContext<
  NotificationCenterContextValue | undefined
>(undefined)

export function useNotificationCenter() {
  const context = useContext(NotificationCenterContext)
  if (!context) {
    throw new Error(
      "useNotificationCenter must be used within NotificationCenterProvider",
    )
  }
  return context
}

export type NotificationCenterProviderProps = { children: ReactNode }

/**
 * Open/closed state of the notification center dock, shared between the
 * sidebar bell (the toggle) and the dock itself (rendered beside the
 * sidebar in AppRouter). Deliberately NOT a URL param: page navigation
 * replaces the search string, and the dock is meant to stay put through
 * an operator's whole triage session — so it persists to localStorage
 * instead and reopens the way it was left.
 */
function NotificationCenterProvider(props: NotificationCenterProviderProps) {
  const { children } = props

  const [isOpen, setIsOpen] = useState(
    () => localStorage.getItem(NOTIFICATION_CENTER_OPEN_STORAGE_KEY) === "1",
  )

  function setOpen(next: boolean) {
    setIsOpen(next)
    localStorage.setItem(NOTIFICATION_CENTER_OPEN_STORAGE_KEY, next ? "1" : "0")
  }

  return (
    <NotificationCenterContext.Provider
      value={{
        isOpen,
        open: () => setOpen(true),
        close: () => setOpen(false),
        toggle: () => setOpen(!isOpen),
      }}
    >
      {children}
    </NotificationCenterContext.Provider>
  )
}

export default NotificationCenterProvider
